import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mic, Square, Download, Trash2, CheckCircle, XCircle, AlertTriangle, RefreshCw, Clock, Zap, MessageSquare, Volume2, Languages, Hand } from 'lucide-react';
import voiceDiagnostics from '../services/vcce/VoiceDiagnostics';

/**
 * SPRINT 3A - Página de Diagnóstico de Voz (v2)
 * 
 * Permite testar a pipeline de voz em qualquer dispositivo e
 * visualizar métricas reais para validar >95% de eventos naturais.
 * 
 * v2: Adicionado Timeline de Conversa e Métricas de Experiência
 */
export default function VoiceDiagnosticsPage() {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState('detecting...');
  const [isRecording, setIsRecording] = useState(false);
  const [events, setEvents] = useState([]);
  const [aggregatedStats, setAggregatedStats] = useState({});
  const [testCount, setTestCount] = useState(0);
  const [activeTab, setActiveTab] = useState('timeline'); // timeline, ux, platforms
  
  // Timeline de conversa completa
  const [conversationTimeline, setConversationTimeline] = useState([]);
  
  // Métricas UX
  const [uxMetrics, setUxMetrics] = useState({
    avgMicOpenTime: 0,
    avgSttTime: 0,
    avgTurnDecisionTime: 0,
    avgLlmResponseTime: 0,
    avgTtsPlayTime: 0,
    avgTotalTime: 0,
    languageMixingCount: 0,
    bargeInTriggeredCount: 0,
    bargeInSuccessCount: 0,
    translationUsedCount: 0,
    testsCompleted: 0,
  });
  
  // Refs para SpeechRecognition
  const recognitionRef = useRef(null);
  const testStartTimeRef = useRef(null);
  const timelineRef = useRef([]);

  useEffect(() => {
    const detected = voiceDiagnostics._detectPlatform();
    setPlatform(detected);
    voiceDiagnostics.startSession(detected);
    setAggregatedStats(voiceDiagnostics.getAggregatedStats());
    
    return () => {
      voiceDiagnostics.endSession();
    };
  }, []);

  const addTimelineEvent = useCallback((phase, status, details = {}) => {
    const now = Date.now();
    const elapsed = testStartTimeRef.current ? now - testStartTimeRef.current : 0;
    
    const event = {
      id: `${phase}_${now}`,
      phase,
      status, // 'start', 'success', 'error', 'info'
      timestamp: now,
      elapsedMs: elapsed,
      details,
    };
    
    timelineRef.current = [...timelineRef.current, event];
    setConversationTimeline([...timelineRef.current]);
    setEvents(prev => [...prev, { type: phase, data: details, time: now }]);
    
    voiceDiagnostics.logEvent(phase, { ...details, elapsedMs: elapsed });
  }, []);

  const updateUxMetrics = useCallback((metrics) => {
    setUxMetrics(prev => {
      const count = prev.testsCompleted + 1;
      return {
        avgMicOpenTime: Math.round((prev.avgMicOpenTime * prev.testsCompleted + (metrics.micOpenTime || 0)) / count),
        avgSttTime: Math.round((prev.avgSttTime * prev.testsCompleted + (metrics.sttTime || 0)) / count),
        avgTurnDecisionTime: Math.round((prev.avgTurnDecisionTime * prev.testsCompleted + (metrics.turnDecisionTime || 0)) / count),
        avgLlmResponseTime: Math.round((prev.avgLlmResponseTime * prev.testsCompleted + (metrics.llmResponseTime || 0)) / count),
        avgTtsPlayTime: Math.round((prev.avgTtsPlayTime * prev.testsCompleted + (metrics.ttsPlayTime || 0)) / count),
        avgTotalTime: Math.round((prev.avgTotalTime * prev.testsCompleted + (metrics.totalTime || 0)) / count),
        languageMixingCount: prev.languageMixingCount + (metrics.languageMixing ? 1 : 0),
        bargeInTriggeredCount: prev.bargeInTriggeredCount + (metrics.bargeInTriggered ? 1 : 0),
        bargeInSuccessCount: prev.bargeInSuccessCount + (metrics.bargeInSuccess ? 1 : 0),
        translationUsedCount: prev.translationUsedCount + (metrics.translationUsed ? 1 : 0),
        testsCompleted: count,
      };
    });
  }, []);

  const startTest = useCallback(async () => {
    try {
      // Reset timeline for new test
      timelineRef.current = [];
      setConversationTimeline([]);
      testStartTimeRef.current = Date.now();
      
      const testMetrics = {
        micOpenTime: 0,
        sttTime: 0,
        turnDecisionTime: 0,
        llmResponseTime: 0,
        ttsPlayTime: 0,
        totalTime: 0,
        languageMixing: false,
        bargeInTriggered: false,
        bargeInSuccess: false,
        translationUsed: false,
      };
      
      // Iniciar interação
      voiceDiagnostics.startInteraction();
      addTimelineEvent('TEST_START', 'start', { platform });
      
      // 1. MIC OPEN
      addTimelineEvent('MIC_REQUESTING', 'start');
      const micStartTime = Date.now();
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      testMetrics.micOpenTime = Date.now() - micStartTime;
      addTimelineEvent('MIC_OPENED', 'success', { 
        tracks: stream.getAudioTracks().length,
        timeMs: testMetrics.micOpenTime 
      });
      
      // 2. STT
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        addTimelineEvent('STT_ERROR', 'error', { message: 'SpeechRecognition not supported' });
        return;
      }
      
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      let sttFinalReceived = false;
      let speechEndReceived = false;
      let audioEndReceived = false;
      let sttStartTime = null;
      let lastTranscript = '';
      let stabilityTimer = null;
      let fallbackTimer = null;
      
      recognition.onstart = () => {
        sttStartTime = Date.now();
        addTimelineEvent('STT_LISTENING', 'start', { language: 'en-US' });
        setIsRecording(true);
      };
      
      recognition.onspeechstart = () => {
        addTimelineEvent('SPEECH_DETECTED', 'info');
      };
      
      recognition.onresult = (event) => {
        const result = event.results[event.results.length - 1];
        const transcript = result[0].transcript;
        const confidence = result[0].confidence;
        const isFinal = result.isFinal;
        
        lastTranscript = transcript;
        
        if (isFinal) {
          sttFinalReceived = true;
          testMetrics.sttTime = Date.now() - sttStartTime;
          addTimelineEvent('STT_FINAL', 'success', { 
            transcript: transcript.substring(0, 50),
            confidence: confidence?.toFixed(2) || 'N/A',
            timeMs: testMetrics.sttTime
          });
          
          // Stability timer (800ms)
          clearTimeout(stabilityTimer);
          stabilityTimer = setTimeout(() => {
            if (!speechEndReceived && !audioEndReceived) {
              testMetrics.turnDecisionTime = Date.now() - (sttStartTime + testMetrics.sttTime);
              addTimelineEvent('TURN_DECISION', 'success', { 
                signal: 'sttStable',
                timeMs: testMetrics.turnDecisionTime
              });
              finishInteraction('sttStable', transcript, testMetrics);
            }
          }, 800);
          
          // Fallback timer (3000ms)
          clearTimeout(fallbackTimer);
          fallbackTimer = setTimeout(() => {
            if (!speechEndReceived && !audioEndReceived) {
              testMetrics.turnDecisionTime = Date.now() - (sttStartTime + testMetrics.sttTime);
              addTimelineEvent('TURN_DECISION', 'error', { 
                signal: 'fallback',
                timeMs: testMetrics.turnDecisionTime
              });
              finishInteraction('fallback', transcript, testMetrics);
            }
          }, 3000);
        } else {
          addTimelineEvent('STT_INTERIM', 'info', { 
            transcript: transcript.substring(0, 30) + '...'
          });
        }
      };
      
      recognition.onspeechend = () => {
        speechEndReceived = true;
        clearTimeout(stabilityTimer);
        clearTimeout(fallbackTimer);
        
        testMetrics.turnDecisionTime = sttStartTime ? Date.now() - (sttStartTime + (testMetrics.sttTime || 0)) : 0;
        addTimelineEvent('SPEECH_END_EVENT', 'success', { timeMs: testMetrics.turnDecisionTime });
        
        if (sttFinalReceived) {
          addTimelineEvent('TURN_DECISION', 'success', { 
            signal: 'speechEnd',
            timeMs: testMetrics.turnDecisionTime
          });
          finishInteraction('speechEnd', lastTranscript, testMetrics);
        }
      };
      
      recognition.onaudioend = () => {
        audioEndReceived = true;
        clearTimeout(stabilityTimer);
        clearTimeout(fallbackTimer);
        
        addTimelineEvent('AUDIO_END_EVENT', 'success');
        
        if (sttFinalReceived && !speechEndReceived) {
          testMetrics.turnDecisionTime = sttStartTime ? Date.now() - (sttStartTime + (testMetrics.sttTime || 0)) : 0;
          addTimelineEvent('TURN_DECISION', 'success', { 
            signal: 'audioEnd',
            timeMs: testMetrics.turnDecisionTime
          });
          finishInteraction('audioEnd', lastTranscript, testMetrics);
        }
      };
      
      recognition.onerror = (event) => {
        addTimelineEvent('STT_ERROR', 'error', { error: event.error });
        cleanup();
      };
      
      recognition.onend = () => {
        addTimelineEvent('RECOGNITION_END', 'info');
        cleanup();
      };
      
      const finishInteraction = (signal, transcript, metrics) => {
        clearTimeout(stabilityTimer);
        clearTimeout(fallbackTimer);
        
        metrics.totalTime = Date.now() - testStartTimeRef.current;
        
        addTimelineEvent('TEST_COMPLETE', 'success', {
          signal,
          transcript: transcript?.substring(0, 50),
          totalTimeMs: metrics.totalTime,
        });
        
        // Update diagnostics
        voiceDiagnostics.logEvent('TURN_DECISION', {
          reason: signal.toUpperCase(),
          endSignal: signal,
          scores: {
            speechEndEvent: speechEndReceived ? 0.18 : 0,
            audioEndEvent: audioEndReceived ? 0.15 : 0,
            sttFinalStable: signal === 'sttStable' ? 0.12 : 0,
          },
        });
        voiceDiagnostics.logEvent('INTERACTION_END', { signal });
        
        // Update UX metrics
        updateUxMetrics(metrics);
        setTestCount(prev => prev + 1);
        updateStats();
      };
      
      const cleanup = () => {
        setIsRecording(false);
        stream.getTracks().forEach(track => track.stop());
      };
      
      recognitionRef.current = recognition;
      recognition.start();
      
    } catch (error) {
      addTimelineEvent('ERROR', 'error', { message: error.message });
      setIsRecording(false);
    }
  }, [addTimelineEvent, platform, updateUxMetrics]);

  const stopTest = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const updateStats = useCallback(() => {
    setAggregatedStats(voiceDiagnostics.getAggregatedStats());
  }, []);

  const exportData = useCallback(() => {
    const data = {
      exportedAt: new Date().toISOString(),
      platform,
      uxMetrics,
      conversationTimeline,
      diagnostics: JSON.parse(voiceDiagnostics.exportData()),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `voice-diagnostics-${platform}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [platform, uxMetrics, conversationTimeline]);

  const clearData = useCallback(() => {
    voiceDiagnostics.clear();
    voiceDiagnostics.startSession(platform);
    setEvents([]);
    setConversationTimeline([]);
    timelineRef.current = [];
    setTestCount(0);
    setAggregatedStats({});
    setUxMetrics({
      avgMicOpenTime: 0,
      avgSttTime: 0,
      avgTurnDecisionTime: 0,
      avgLlmResponseTime: 0,
      avgTtsPlayTime: 0,
      avgTotalTime: 0,
      languageMixingCount: 0,
      bargeInTriggeredCount: 0,
      bargeInSuccessCount: 0,
      translationUsedCount: 0,
      testsCompleted: 0,
    });
  }, [platform]);

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'MIC_REQUESTING':
      case 'MIC_OPENED':
        return <Mic className="w-4 h-4" />;
      case 'STT_LISTENING':
      case 'STT_INTERIM':
      case 'STT_FINAL':
        return <MessageSquare className="w-4 h-4" />;
      case 'SPEECH_DETECTED':
      case 'SPEECH_END_EVENT':
        return <Volume2 className="w-4 h-4" />;
      case 'TURN_DECISION':
        return <Zap className="w-4 h-4" />;
      case 'BARGE_IN':
        return <Hand className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      case 'start': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getSignalBadge = (signal) => {
    const colors = {
      speechEnd: 'bg-green-500/20 text-green-400 border-green-500/30',
      audioEnd: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      sttStable: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      fallback: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[signal] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  return (
    <div className="min-h-screen bg-dark text-white p-4">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-dark-card">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Voice Diagnostics</h1>
          <p className="text-sm text-gray-400">SPRINT 3A - Platform Testing v2</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">Platform</p>
          <p className="text-sm font-bold text-vocalira-400">{platform}</p>
        </div>
      </div>

      {/* Test Controls */}
      <div className="bg-dark-card rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-bold">Tests: {testCount}</p>
            <p className="text-xs text-gray-400">Speak a phrase and see the pipeline</p>
          </div>
          {!isRecording ? (
            <button
              onClick={startTest}
              className="flex items-center gap-2 px-4 py-2 bg-vocalira-500 hover:bg-vocalira-600 rounded-xl font-bold transition-colors"
            >
              <Mic className="w-5 h-5" />
              Start Test
            </button>
          ) : (
            <button
              onClick={stopTest}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-xl font-bold transition-colors animate-pulse"
            >
              <Square className="w-5 h-5" />
              Recording...
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'timeline', label: 'Timeline' },
          { id: 'ux', label: 'UX Metrics' },
          { id: 'platforms', label: 'Platforms' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
              activeTab === tab.id 
                ? 'bg-vocalira-500 text-white' 
                : 'bg-dark-card text-gray-400 hover:bg-dark-elevated'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="bg-dark-card rounded-xl p-4 mb-4">
          <h2 className="font-bold mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-vocalira-400" />
            Conversation Timeline
          </h2>
          
          {conversationTimeline.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">
              No timeline yet. Start a test to see the conversation flow.
            </p>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-dark-border" />
              
              {/* Timeline events */}
              <div className="space-y-3">
                {conversationTimeline.map((event, i) => (
                  <div key={event.id} className="flex items-start gap-3 pl-2">
                    {/* Dot */}
                    <div className={`w-5 h-5 rounded-full ${getStatusColor(event.status)} flex items-center justify-center z-10`}>
                      {getPhaseIcon(event.phase)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 bg-dark-elevated rounded-lg p-3 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">{event.phase}</span>
                        <span className="text-xs text-gray-500">{event.elapsedMs}ms</span>
                      </div>
                      
                      {event.details && Object.keys(event.details).length > 0 && (
                        <div className="mt-1 text-xs text-gray-400">
                          {event.details.signal && (
                            <span className={`inline-block px-2 py-0.5 rounded border ${getSignalBadge(event.details.signal)}`}>
                              {event.details.signal}
                            </span>
                          )}
                          {event.details.transcript && (
                            <p className="mt-1 truncate">&quot;{event.details.transcript}&quot;</p>
                          )}
                          {event.details.timeMs !== undefined && (
                            <p className="mt-1">Duration: {event.details.timeMs}ms</p>
                          )}
                          {event.details.confidence && (
                            <p>Confidence: {event.details.confidence}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* UX Metrics Tab */}
      {activeTab === 'ux' && (
        <div className="space-y-4">
          {/* Timing Metrics */}
          <div className="bg-dark-card rounded-xl p-4">
            <h2 className="font-bold mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Timing Metrics (Average)
            </h2>
            
            <div className="grid grid-cols-2 gap-3">
              <MetricCard label="Mic Open" value={uxMetrics.avgMicOpenTime} unit="ms" target={500} />
              <MetricCard label="STT Time" value={uxMetrics.avgSttTime} unit="ms" target={2000} />
              <MetricCard label="Turn Decision" value={uxMetrics.avgTurnDecisionTime} unit="ms" target={1000} />
              <MetricCard label="Total Time" value={uxMetrics.avgTotalTime} unit="ms" target={5000} />
            </div>
          </div>

          {/* Experience Metrics */}
          <div className="bg-dark-card rounded-xl p-4">
            <h2 className="font-bold mb-3 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-400" />
              Experience Metrics
            </h2>
            
            <div className="space-y-2">
              <ExperienceRow 
                label="Language Mixing" 
                count={uxMetrics.languageMixingCount}
                total={uxMetrics.testsCompleted}
                isError={true}
              />
              <ExperienceRow 
                label="Barge-in Triggered" 
                count={uxMetrics.bargeInTriggeredCount}
                total={uxMetrics.testsCompleted}
              />
              <ExperienceRow 
                label="Barge-in Success" 
                count={uxMetrics.bargeInSuccessCount}
                total={uxMetrics.bargeInTriggeredCount || 1}
              />
              <ExperienceRow 
                label="Translation Used" 
                count={uxMetrics.translationUsedCount}
                total={uxMetrics.testsCompleted}
              />
            </div>
          </div>

          {/* Signal Distribution */}
          <div className="bg-dark-card rounded-xl p-4">
            <h2 className="font-bold mb-3">Signal Distribution (This Platform)</h2>
            
            {aggregatedStats[platform] ? (
              <div className="space-y-2">
                <SignalBar label="speechEnd" count={aggregatedStats[platform].breakdown?.speechEnd || 0} total={aggregatedStats[platform].interactions || 1} color="green" />
                <SignalBar label="audioEnd" count={aggregatedStats[platform].breakdown?.audioEnd || 0} total={aggregatedStats[platform].interactions || 1} color="blue" />
                <SignalBar label="sttStable" count={aggregatedStats[platform].breakdown?.sttStable || 0} total={aggregatedStats[platform].interactions || 1} color="yellow" />
                <SignalBar label="fallback" count={aggregatedStats[platform].breakdown?.fallback || 0} total={aggregatedStats[platform].interactions || 1} color="red" />
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No data yet. Run some tests.</p>
            )}
          </div>
        </div>
      )}

      {/* Platforms Tab */}
      {activeTab === 'platforms' && (
        <div className="space-y-4">
          {/* Current Platform Stats */}
          <div className="bg-dark-card rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold">Results by Platform</h2>
              <button onClick={updateStats} className="p-2 text-gray-400 hover:text-white">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            
            {Object.keys(aggregatedStats).length === 0 ? (
              <p className="text-gray-500 text-sm">No data yet. Run some tests.</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(aggregatedStats).map(([plat, stats]) => (
                  <div key={plat} className="border border-dark-border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {stats.meetsTarget ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-400" />
                        )}
                        <span className="font-bold">{plat}</span>
                      </div>
                      <span className={`font-bold ${
                        parseFloat(stats.naturalPercentage) >= 95 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {stats.naturalPercentage}% natural
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-gray-400">Total:</span> {stats.interactions}</div>
                      <div><span className="text-gray-400">Natural:</span> {stats.natural}</div>
                      <div><span className="text-gray-400">Fallback:</span> <span className={stats.fallback > 0 ? 'text-red-400' : ''}>{stats.fallback}</span></div>
                    </div>
                    
                    <div className="mt-2 flex gap-2 text-xs flex-wrap">
                      <span className="text-green-400">speechEnd: {stats.breakdown?.speechEnd || 0}</span>
                      <span className="text-blue-400">audioEnd: {stats.breakdown?.audioEnd || 0}</span>
                      <span className="text-yellow-400">sttStable: {stats.breakdown?.sttStable || 0}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Target Checklist */}
          <div className="bg-dark-card rounded-xl p-4">
            <h2 className="font-bold mb-3">Target: 95%+ Natural Events</h2>
            
            <div className="space-y-2">
              {['desktop-chrome', 'desktop-edge', 'android-chrome', 'android-samsung', 'ios-safari', 'ios-chrome', 'pwa-android', 'pwa-ios'].map(plat => {
                const stats = aggregatedStats[plat];
                const hasData = stats && stats.interactions > 0;
                const meetsTarget = hasData && parseFloat(stats.naturalPercentage) >= 95;
                
                return (
                  <div key={plat} className="flex items-center justify-between py-1">
                    <span className="text-sm">{plat}</span>
                    <div className="flex items-center gap-2">
                      {hasData ? (
                        <>
                          <span className={`text-sm font-mono ${meetsTarget ? 'text-green-400' : 'text-red-400'}`}>
                            {stats.naturalPercentage}%
                          </span>
                          {meetsTarget ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-gray-500">Not tested</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 mt-4">
        <button
          onClick={exportData}
          className="flex-1 flex items-center justify-center gap-2 py-3 bg-dark-elevated hover:bg-dark-border rounded-xl transition-colors"
        >
          <Download className="w-5 h-5" />
          Export JSON
        </button>
        <button
          onClick={clearData}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-4 p-4 bg-dark-elevated rounded-xl text-sm text-gray-400">
        <h3 className="font-bold text-white mb-2">Testing Protocol</h3>
        <ol className="list-decimal list-inside space-y-1">
          <li>Run at least 10 tests per platform</li>
          <li>Check Timeline for conversation flow</li>
          <li>Check UX Metrics for timing analysis</li>
          <li>Check Platforms for 95%+ natural target</li>
          <li>Export JSON for detailed analysis</li>
        </ol>
      </div>
    </div>
  );
}

// Helper Components
function MetricCard({ label, value, unit, target }) {
  const isGood = value <= target;
  return (
    <div className="bg-dark-elevated rounded-lg p-3">
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`text-lg font-bold ${isGood ? 'text-green-400' : 'text-yellow-400'}`}>
        {value}{unit}
      </p>
      <p className="text-xs text-gray-500">Target: &lt;{target}{unit}</p>
    </div>
  );
}

function ExperienceRow({ label, count, total, isError = false }) {
  const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
  const isProblematic = isError && count > 0;
  
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm">{label}</span>
      <span className={`text-sm font-mono ${isProblematic ? 'text-red-400' : 'text-gray-400'}`}>
        {count}/{total} ({percentage}%)
      </span>
    </div>
  );
}

function SignalBar({ label, count, total, color }) {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const colorClass = {
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  }[color];
  
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 text-xs text-gray-400">{label}</span>
      <div className="flex-1 h-4 bg-dark-elevated rounded overflow-hidden">
        <div className={`h-full ${colorClass}`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="w-12 text-xs text-right">{count} ({percentage.toFixed(0)}%)</span>
    </div>
  );
}
